import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/**
 * TanStack Query client with optimized defaults for Fable
 *
 * Configuration:
 * - staleTime: 5 minutes - Data considered fresh, no refetch
 * - gcTime: 30 minutes - Cache retention after component unmount
 * - refetchOnWindowFocus: false - Don't refetch when tab regains focus
 * - retry: 2 - Retry failed requests twice before giving up
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,      // 5 minutes before data is considered stale
      gcTime: 1000 * 60 * 30,        // 30 minutes cache retention (formerly cacheTime)
      refetchOnWindowFocus: false,   // Don't refetch on tab focus (reduces API calls)
      retry: 2,                      // Retry failed requests twice
      refetchOnMount: false,         // Don't refetch if data exists and isn't stale
    },
    mutations: {
      retry: 1,                      // Retry mutations once on failure
    },
  },
});

interface QueryProviderProps {
  children: React.ReactNode;
}

/**
 * QueryProvider wraps the app with TanStack Query's QueryClientProvider
 * and includes DevTools in development mode for debugging.
 */
export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* DevTools only shown in development */}
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  );
};

export { queryClient };
export default QueryProvider;
